import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import packagingWarehouse from '../class/packagingWarehouse';
import { formatRpcError } from '../class/jrpc20WSClient';
import { genOperationId, toNum, fmtQty, perColloOf, isPartialRow, sameQty } from '../class/util';
import swal from 'sweetalert';
import PkgSelect from './component/PkgSelect';

// Prelievo materiale per ordine. L'operatore è il magazziniere loggato.
//
// Il MODO è IMPLICITO NELLA RIGA scelta, non è più una tendina:
//  - riga INTERI: quanti colli prendere + (opzionale) apri UN collo e prelevane una parte.
//    Aprendo un collo nasce una riga collo-aperto con i pezzi che restano.
//  - riga COLLO APERTO: quanti pezzi prendere da quel collo. A residuo 0 la riga si chiude.
// I pezzi dei colli interi sono DERIVATI dalla pezzatura, mai digitati.

// Riga UI → payload BE. `picked` è derivato e serve a UI/anteprima: il BE lo ricalcola dalla
// pezzatura memorizzata, che resta l'unica fonte di verità.
//
// Sul bucket interi ogni collo da aprire ha il SUO campo pezzi (`opens`): ognuno diventa una riga
// col proprio residuo, quindi un totale unico non basterebbe a ricostruirli.
function readPick(f, av) {
    if (av.isPartial) {
        const pp = toNum(f.piecesPicked);
        return { bucket: 'partial', pieces_picked: pp, picked: pp };
    }
    const colliFull = toNum(f.colliFull);
    const opens = f.opens.map(v => ({ pieces_picked: toNum(v) }));
    const fromOpens = opens.reduce((sum, o) => sum + o.pieces_picked, 0);
    return { bucket: 'whole', colli_full: colliFull, opens, picked: colliFull * av.pc + fromOpens };
}
function pickIsEmpty(p) {
    if (p.bucket === 'partial') return p.pieces_picked <= 0;
    return p.colli_full === 0 && p.opens.length === 0;
}
// Stesse regole e STESSE STRINGHE del gestionale (packagingStock.js::validatePick).
function validatePick(p, av) {
    if (p.bucket === 'partial') {
        if (p.pieces_picked <= 0) return 'Indicare i pezzi da prelevare';
        if (p.pieces_picked > av.qp) return 'Pezzi superiori al residuo del collo aperto';
        return '';
    }
    if (av.pc <= 0) return 'Pezzi per collo non impostati per questa giacenza: correggerla prima di prelevare';
    if ((p.colli_full + p.opens.length) < 1) return 'Indicare i colli da prelevare';
    if ((p.colli_full + p.opens.length) > av.ci) return 'Colli superiori ai disponibili';
    for (let i = 0; i < p.opens.length; i++) {
        if (p.opens[i].pieces_picked > av.pc) return `Pezzi superiori al contenuto del collo ${fmtQty(i + 1)}`;
    }
    return '';
}
function pickSummary(p, pc) {
    if (p.bucket === 'partial') return `Collo aperto: ${fmtQty(p.pieces_picked)} pz`;
    const parts = [];
    if (p.colli_full > 0) {
        parts.push(`${fmtQty(p.colli_full)} ${p.colli_full === 1 ? 'collo' : 'colli'} × ${fmtQty(pc)} = ${fmtQty(p.colli_full * pc)} pz`);
    }
    if (p.opens.length > 0) {
        parts.push(`apre ${fmtQty(p.opens.length)} ${p.opens.length === 1 ? 'collo' : 'colli'}: ${p.opens.map(o => fmtQty(o.pieces_picked)).join('+')} pz`);
    }
    return `Interi: ${parts.join(' · ')}`;
}
// Gruppo = stessa cella, lotto e pezzatura (la stessa chiave della consultazione aggregata).
function sameGroup(a, b) {
    return String(a.position_uuid) === String(b.position_uuid)
        && String(a.lotto == null ? '' : a.lotto) === String(b.lotto == null ? '' : b.lotto)
        && sameQty(perColloOf(a), perColloOf(b));
}

const PrelievoPage = () => {
    const navigate = useNavigate();
    const ws = new packagingWarehouse();

    const [orders, setOrders] = useState([]);
    const [orderUuid, setOrderUuid] = useState('');
    const [order, setOrder] = useState(null);
    const [components, setComponents] = useState([]);
    const [componentUuid, setComponentUuid] = useState('');
    const [rows, setRows] = useState(null);            // giacenze per divisione+codice
    const [activeRow, setActiveRow] = useState(null);  // stock row in prelievo
    // Un solo form per riga: il modo è dato dalla riga, non si scegli in una tendina.
    const [form, setForm] = useState({ colliFull: '', opens: [], piecesPicked: '' });
    const [cart, setCart] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        ws.getOrders().then(o => setOrders(o.orders || [])).catch(err => swal('Errore', formatRpcError(err), 'error'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onOrderChange = (uuid) => {
        setOrderUuid(uuid); setComponentUuid(''); setRows(null); setActiveRow(null); setCart([]); setComponents([]);
        const o = orders.find(x => x.order_uuid === uuid) || null;
        setOrder(o);
        if (o && o.division_uuid) {
            ws.getComponentsForDivision(o.division_uuid).then(r => setComponents(r.components || [])).catch(err => swal('Errore', formatRpcError(err), 'error'));
        }
    };

    const onComponentChange = (uuid) => {
        setComponentUuid(uuid); setRows(null); setActiveRow(null);
        const comp = components.find(c => c.uuid === uuid);
        if (comp && order && order.division_uuid) {
            ws.getPositionsByCode(order.division_uuid, comp.code).then(r => setRows(r.rows || [])).catch(err => swal('Errore', formatRpcError(err), 'error'));
        }
    };

    // Colli che il carrello APRIRÀ su questa riga: righe di giacenza che ancora non esistono
    // (nascono confermando il prelievo), quindi si mostrano ma non sono prelevabili.
    const pendingOpensOf = (r) => {
        const pc = perColloOf(r);
        if (pc <= 0) return [];
        return cart
            .filter(c => c.stock_uuid === r.stock_uuid && c.bucket === 'whole' && c.opens)
            .flatMap(c => c.opens.map(o => pc - (o.pieces_picked || 0)))
            .filter(rest => rest > 0);
    };

    // Disponibilità effettiva della riga = giacenza − pick già a carrello per lo stesso stock.
    // `pc` (pezzatura) NON si decrementa: portare via colli interi non cambia quanti pezzi ci
    // sono in quelli che restano.
    const availOf = (r) => {
        let ci = Math.round(Number(r.qty_colli_whole) || 0);
        let qp = Number(r.qty_pieces_partial) || 0;
        const pc = perColloOf(r);
        cart.filter(c => c.stock_uuid === r.stock_uuid).forEach(c => {
            if (c.bucket === 'partial') { qp -= c.pieces_picked; }
            // Ogni collo aperto esce dal bucket interi tanto quanto uno preso intero.
            else { ci -= (c.colli_full + c.opens.length); }
        });
        // I pezzi interi si DERIVANO dai colli (invariante), non si scalano a parte.
        return { ci, qi: ci * pc, qp, pc, isPartial: isPartialRow(r) };
    };

    const openPick = (r) => {
        setActiveRow(r);
        setForm({ colliFull: '', opens: [], piecesPicked: '' });
    };
    const updForm = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
    // Colli da aprire: si aggiungono UNO PER CLICK, non con un contatore. Ognuno diventa una riga
    // di giacenza col proprio residuo, quindi ognuno ha il suo campo pezzi.
    const addOpen = () => setForm(prev => ({ ...prev, opens: [...prev.opens, ''] }));
    const removeOpen = (i) => setForm(prev => ({ ...prev, opens: prev.opens.filter((_, idx) => idx !== i) }));
    const updOpen = (i, v) => setForm(prev => ({ ...prev, opens: prev.opens.map((x, idx) => idx === i ? v : x) }));

    // Anteprima + errore del form corrente rispetto alla disponibilità della riga.
    const computeEditor = () => {
        if (!activeRow) return { pick: null, err: '', picked: 0 };
        const a = availOf(activeRow);
        const p = readPick(form, a);
        const err = pickIsEmpty(p) ? 'Indicare una quantità da prelevare' : validatePick(p, a);
        return { pick: p, err, picked: p.picked, av: a };
    };

    // `pc` viaggia sulla voce: serve a ricalcolare i pezzi quando due voci si fondono.
    const cartEntry = (r, pick, pc) => ({
        stock_uuid: r.stock_uuid,
        code: r.__code,
        position_cella: r.position_cella,
        lotto: r.lotto,
        pc,
        summary: pickSummary(pick, pc),
        ...pick,
    });

    // Le righe del carrello si AGGREGANO per riga di giacenza: aggiungere un altro collo dallo
    // stesso stock somma alla voce esistente invece di creare un doppione.
    // Nessun controllo extra sui massimi: la validazione lavora già sulla disponibilità al netto
    // del carrello, quindi la somma non può sforare.
    const mergeCart = (prev, entries) => {
        const out = prev.map(c => ({ ...c }));
        entries.forEach(e => {
            // Il bucket è determinato dalla riga, quindi lo stock_uuid basta a identificare la voce.
            const i = out.findIndex(c => c.stock_uuid === e.stock_uuid);
            if (i < 0) { out.push({ ...e }); return; }
            const cur = out[i];
            if (cur.bucket === 'partial') {
                cur.pieces_picked += e.pieces_picked;
                cur.picked = cur.pieces_picked;
            } else {
                cur.colli_full += e.colli_full;
                // Ogni collo aperto resta una voce a sé: concatenare, non sommare i pezzi.
                cur.opens = [...cur.opens, ...e.opens];
                cur.picked = cur.colli_full * cur.pc + cur.opens.reduce((sum, o) => sum + o.pieces_picked, 0);
            }
            cur.summary = pickSummary(cur, cur.pc);
        });
        return out;
    };

    const addToCart = () => {
        const t = computeEditor();
        if (t.err) { swal('Errore', t.err, 'error'); return; }
        // `pick.picked` è già la quantità prelevata (derivata sui colli interi) e arriva dallo spread.
        setCart(prev => mergeCart(prev, [cartEntry(activeRow, t.pick, t.av.pc)]));
        setActiveRow(null);
    };

    // "Tutto": svuota la riga senza passare dal form. Su una riga interi = tutti i colli interi,
    // su un collo aperto = tutto il residuo. È il caso "solo tutti gli interi / solo i parziali".
    const pickAllOf = (rows) => {
        const entries = [];
        // Disponibilità calcolata progressivamente: più righe dello stesso stock nello stesso giro
        // devono vedere ciò che le precedenti hanno già messo a carrello.
        const takenColli = {}, takenPieces = {};
        rows.forEach(r => {
            const a = availOf(r);
            if (a.isPartial) {
                const qp = a.qp - (takenPieces[r.stock_uuid] || 0);
                if (qp <= 0) return;
                takenPieces[r.stock_uuid] = (takenPieces[r.stock_uuid] || 0) + qp;
                entries.push(cartEntry(r, { bucket: 'partial', pieces_picked: qp, picked: qp }, a.pc));
            } else {
                if (a.pc <= 0) return;
                const ci = a.ci - (takenColli[r.stock_uuid] || 0);
                if (ci <= 0) return;
                takenColli[r.stock_uuid] = (takenColli[r.stock_uuid] || 0) + ci;
                entries.push(cartEntry(r, { bucket: 'whole', colli_full: ci, opens: [], picked: ci * a.pc }, a.pc));
            }
        });
        if (!entries.length) { swal('Attenzione', 'Niente da prelevare', 'warning'); return; }
        setCart(prev => mergeCart(prev, entries));
        setActiveRow(null);
    };

    const cartTotal = cart.reduce((s, c) => s + (Number(c.picked) || 0), 0);
    // Pezzatura della riga in lavorazione: alimenta i valori derivati dei modi 'whole' e 'open'.
    // Disponibilità della riga in lavorazione: alimenta form, derivati e titolo.
    const activeAvail = activeRow ? availOf(activeRow) : { ci: 0, qi: 0, qp: 0, pc: 0, isPartial: false };

    const confirm = async () => {
        if (!cart.length) { swal('Attenzione', 'Nessuna riga da prelevare', 'warning'); return; }
        const ok = await swal({
            title: 'Conferma Prelievo',
            text: `Confermare il prelievo di ${cart.length} riga/righe (${fmtQty(cartTotal)} pz)?`,
            icon: 'info', buttons: ['Annulla', 'Conferma'],
        });
        if (!ok) return;
        setSaving(true);
        try {
            // Bucket 'whole': si inviano SOLO i colli e i pezzi presi dal collo aperto. Pezzi totali
            // e pezzi lasciati li deriva il BE dalla pezzatura memorizzata.
            const picks = cart.map(c => c.bucket === 'partial'
                ? { stock_uuid: c.stock_uuid, bucket: 'partial', pieces_picked: c.pieces_picked }
                : { stock_uuid: c.stock_uuid, bucket: 'whole', colli_full: c.colli_full, opens: c.opens });
            const res = await ws.pickMaterial({ order_uuid: orderUuid, client_operation_id: genOperationId(), picks });
            const saved = (res && res.movements_saved != null) ? res.movements_saved : cart.length;
            await swal('Prelievo effettuato', res && res.idempotent ? 'Prelievo già registrato in precedenza.' : `Registrati ${saved} movimento/i · ${fmtQty(cartTotal)} pz.`, 'success');
            navigate('/home');
        } catch (err) {
            swal('Errore', formatRpcError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    // Codice della riga attiva (per il carrello): lo prendiamo dal componente selezionato.
    const selectedCode = (components.find(c => c.uuid === componentUuid) || {}).code || '';
    const rowsWithCode = (rows || []).map(r => ({ ...r, __code: selectedCode }));
    const editorPreview = computeEditor();

    return (
        <div className="pkg-page">
            <div className="pkg-page-header">
                <h2 className="pkg-title">Prelievo Materiale</h2>
            </div>

            <div className="pkg-card">
                <div className="pkg-row">
                    <div className="pkg-field">
                        <label>Ordine</label>
                        <PkgSelect
                            value={orderUuid}
                            options={orders.map(o => ({ value: o.order_uuid, label: `${o.order_production_number} — ${o.article_code} (${o.client_name}) [${o.status_label}]` }))}
                            onChange={onOrderChange}
                            placeholder="Cerca ordine..."
                            noOptionsText="Nessun ordine"
                        />
                    </div>
                    <div className="pkg-field">
                        <label>Divisione</label>
                        <input className="pkg-readonly" readOnly value={order ? (order.division_name || '(ordine senza divisione)') : ''} placeholder="—" />
                        <span className="pkg-hint">Ricavata dall'ordine</span>
                    </div>
                    <div className="pkg-field">
                        <label>Codice articolo</label>
                        <PkgSelect
                            value={componentUuid}
                            options={components.map(c => ({ value: c.uuid, label: `${c.code} - ${c.description}` }))}
                            onChange={onComponentChange}
                            isDisabled={!order || !order.division_uuid}
                            placeholder={order ? (components.length ? 'Cerca articolo...' : 'Nessun articolo a giacenza') : "Seleziona prima l'ordine"}
                            noOptionsText="Nessun articolo"
                        />
                    </div>
                </div>
            </div>

            {rows !== null && (
                <div className="pkg-card">
                    <div className="pkg-section-title">Posizioni disponibili</div>
                    <div className="pkg-table-wrap">
                        <table>
                            <thead>
                                <tr className="table-head"><th>Posizione</th><th>Lotto</th><th className="text-right">Colli interi</th><th className="text-right">Collo aperto</th><th className="text-right">Tot.</th><th></th></tr>
                            </thead>
                            <tbody>
                                {rowsWithCode.map(r => {
                                    const a = availOf(r);
                                    return (
                                        <tr key={r.stock_uuid}>
                                            {/* Un collo aperto è una riga a sé, rientrata: si legge come figlia
                                                della riga interi invece che come un duplicato. */}
                                            <td>{a.isPartial && <span className="muted">└ </span>}{r.position_cella}</td>
                                            <td>{r.lotto}</td>
                                            {/* La pezzatura è in chiave: qui possono comparire più righe con
                                                stessa cella e stesso lotto, distinte SOLO da questa. Senza il
                                                "× N" sembrerebbero duplicati e si preleverebbe dalla sbagliata. */}
                                            <td className="text-right">
                                                {a.isPartial ? <span className="muted">—</span> : <>
                                                    {fmtQty(a.ci)} <span className="muted">
                                                        {a.pc > 0 ? `× ${fmtQty(a.pc)} = ${fmtQty(a.qi)} pz` : `(${fmtQty(a.qi)} pz)`}
                                                    </span>
                                                </>}
                                            </td>
                                            {/* Sui colli aperti si mostra anche DA QUANDO sono aperti: l'operatore
                                                scegli liberamente, ma le righe arrivano ordinate dal più vecchio. */}
                                            <td className="text-right">
                                                {a.isPartial ? <>
                                                    {fmtQty(a.qp)} pz
                                                    {r.created_at ? <span className="muted"> · aperto {new Date(Number(r.created_at) * 1000).toLocaleDateString('it-IT')}</span> : null}
                                                </> : (() => {
                                                    // Sulla riga interi: i colli che il prelievo a carrello sta per
                                                    // aprire. Non prelevabili (le righe non esistono ancora), quindi
                                                    // in grigio e senza azioni.
                                                    const pend = pendingOpensOf(r);
                                                    if (!pend.length) return <span className="muted">—</span>;
                                                    return (
                                                        <span className="muted">
                                                            → {fmtQty(pend.length)} {pend.length === 1 ? 'collo' : 'colli'} in apertura:{' '}
                                                            {pend.map(x => fmtQty(x)).join(' + ')} pz
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="text-right"><strong>{fmtQty(a.qi + a.qp)}</strong></td>
                                            {/* Azioni in una barra flex: su tablet possono andare a capo,
                                                ma restano allineate e con spaziatura costante. */}
                                            <td>
                                                <div className="pkg-row-actions">
                                                    <button className="btn-sm blue_butt" onClick={() => openPick(r)} disabled={(a.qi + a.qp) <= 0}>Preleva</button>
                                                    <button className="btn-sm btn-indietro" onClick={() => pickAllOf([r])}
                                                        disabled={(a.isPartial ? a.qp : a.ci) <= 0}
                                                        title={a.isPartial ? 'Preleva tutto il residuo di questo collo' : 'Preleva tutti i colli interi'}>
                                                        Tutto
                                                    </button>
                                                    {/* Il bottone di gruppo va UNA volta sola: sulla prima riga del gruppo.
                                                        Il totale sta nel title, così l'etichetta resta corta. */}
                                                    {(() => {
                                                        const members = rowsWithCode.filter(x => sameGroup(x, r));
                                                        if (members.length < 2 || members[0].stock_uuid !== r.stock_uuid) return null;
                                                        const tot = members.reduce((sum, x) => {
                                                            const av = availOf(x);
                                                            return sum + (av.isPartial ? av.qp : av.ci * av.pc);
                                                        }, 0);
                                                        return (
                                                            <button className="btn-sm btn-indietro" onClick={() => pickAllOf(members)}
                                                                title={`Preleva interi e tutti i colli aperti di questa cella/lotto: ${fmtQty(tot)} pz`}>
                                                                Tutto il lotto
                                                            </button>
                                                        );
                                                    })()}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {rowsWithCode.length === 0 && <tr><td colSpan="6" style={{ textAlign: 'center', padding: '20px' }} className="muted">Nessuna giacenza per questo codice in questa divisione.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeRow && (
                <div className="pkg-card">
                    <div className="pkg-section-title">
                        {activeAvail.isPartial ? 'Prelievo dal collo aperto in ' : 'Prelievo da '}
                        {activeRow.position_cella}{activeRow.lotto ? ` · Lotto ${activeRow.lotto}` : ''}
                        {activeAvail.pc > 0 ? ` · colli da ${fmtQty(activeAvail.pc)} pz` : ''}
                        {activeAvail.isPartial ? ` · residuo ${fmtQty(activeAvail.qp)} pz` : ` · ${fmtQty(activeAvail.ci)} colli disponibili`}
                    </div>
                    {/* Un solo form: il modo è dato dalla RIGA scelta, non da una tendina. */}
                    {activeAvail.isPartial ? (
                        <div className="pkg-row">
                            <div className="pkg-field">
                                <label>Pezzi da prelevare</label>
                                <input className="pkg-int" inputMode="decimal" value={form.piecesPicked}
                                    onChange={e => updForm('piecesPicked', e.target.value)} placeholder="0" />
                                <button className="btn-sm btn-indietro" onClick={() => updForm('piecesPicked', fmtQty(activeAvail.qp))}
                                    disabled={activeAvail.qp <= 0}>
                                    Tutto ({fmtQty(activeAvail.qp)} pz)
                                </button>
                                <span className="pkg-hint">Prelevando tutto il residuo il collo si chiude e la riga sparisce dalla giacenza.</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="pkg-row pkg-row-pair">
                                <div className="pkg-field">
                                    <label>N° colli interi</label>
                                    <input className="pkg-int" inputMode="numeric" value={form.colliFull}
                                        onChange={e => updForm('colliFull', e.target.value)} placeholder="0" />
                                    {/* Scorciatoia DEL CAMPO colli: sta sotto l'input che riempie, non
                                        sotto i pezzi calcolati (che sono un risultato, non un campo). */}
                                    <button className="btn-sm btn-indietro"
                                        onClick={() => setForm(prev => ({ ...prev, colliFull: fmtQty(activeAvail.ci), opens: [] }))}
                                        disabled={activeAvail.ci <= 0}>
                                        Tutti ({fmtQty(activeAvail.ci)})
                                    </button>
                                </div>
                                <div className="pkg-field">
                                    <label>Pezzi (calcolati)</label>
                                    <div className="pkg-derived-line">
                                        {activeAvail.pc > 0
                                            ? `${fmtQty(toNum(form.colliFull) * activeAvail.pc)} pz (${fmtQty(toNum(form.colliFull))} × ${fmtQty(activeAvail.pc)}/collo)`
                                            : 'Pezzi per collo non impostati'}
                                    </div>
                                </div>
                            </div>
                            {/* Ogni click apre UN collo: ognuno diventa una riga di giacenza col suo
                                residuo, quindi ha il suo campo pezzi. Non un contatore. */}
                            <div className="pkg-row">
                                <div className="pkg-field">
                                    <label>Colli da aprire</label>
                                    <span className="pkg-hint">Ogni click apre un collo: diventerà una riga di giacenza con il suo residuo.</span>
                                    <button className="btn-sm btn-indietro" onClick={addOpen}
                                        disabled={(activeAvail.ci - toNum(form.colliFull) - form.opens.length) < 1}>
                                        + Apri un collo
                                    </button>
                                </div>
                            </div>
                            {/* Campo + residuo + Rimuovi su UNA riga (vedi .pkg-row-open). */}
                            {form.opens.map((v, i) => (
                                <div className="pkg-row pkg-row-open" key={i}>
                                    <div className="pkg-field">
                                        <label>Collo {fmtQty(i + 1)} — pezzi prelevati</label>
                                        <input className="pkg-int" inputMode="decimal" value={v}
                                            onChange={e => updOpen(i, e.target.value)} placeholder="0" />
                                    </div>
                                    <div className="pkg-derived-line">
                                        {activeAvail.pc <= 0
                                            ? 'Pezzi per collo non impostati'
                                            : (activeAvail.pc - toNum(v) > 0
                                                ? `restano ${fmtQty(activeAvail.pc - toNum(v))} pz → nuova riga collo aperto`
                                                : 'collo svuotato → conta come collo intero')}
                                    </div>
                                    <button className="btn-sm btn-indietro" onClick={() => removeOpen(i)}
                                        title="Non aprire questo collo">
                                        Rimuovi
                                    </button>
                                </div>
                            ))}
                        </>
                    )}
                    <div className={`pkg-banner ${editorPreview.err ? 'pkg-banner-warn' : 'pkg-banner-info'}`}>
                        {editorPreview.err ? `⚠ ${editorPreview.err}` : `Prelevi ${fmtQty(editorPreview.picked)} pz`}
                    </div>
                    <div className="pkg-actions-right">
                        <button className="btn-indietro" onClick={() => setActiveRow(null)}>Annulla</button>
                        <button className="blue_butt" onClick={addToCart}>Aggiungi al prelievo</button>
                    </div>
                </div>
            )}

            {cart.length > 0 && (
                <div className="pkg-card">
                    <div className="pkg-section-title">Righe da prelevare</div>
                    <div className="pkg-table-wrap">
                        <table>
                            <thead>
                                <tr className="table-head"><th>Codice</th><th>Posizione</th><th>Lotto</th><th>Dettaglio</th><th className="text-right">Qtà</th><th></th></tr>
                            </thead>
                            <tbody>
                                {cart.map((c, i) => (
                                    <tr key={i}>
                                        <td>{c.code}</td><td>{c.position_cella}</td><td>{c.lotto}</td><td>{c.summary}</td>
                                        <td className="text-right">{fmtQty(c.picked)}</td>
                                        <td><button className="btn-remove" onClick={() => setCart(prev => prev.filter((_, idx) => idx !== i))}>×</button></td>
                                    </tr>
                                ))}
                                <tr className="pkg-total-row"><td colSpan="4" className="text-right">Totale da prelevare</td><td className="text-right"><strong>{fmtQty(cartTotal)}</strong></td><td></td></tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="pkg-actions-right">
                        <button className="blue_butt confirm-btn" onClick={confirm} disabled={saving}>{saving ? 'Salvataggio...' : 'Conferma Prelievo'}</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PrelievoPage;
